import {
  BoxGeometry,
  BufferGeometry,
  CylinderGeometry,
  DoubleSide,
  Euler,
  LatheGeometry,
  Material,
  Matrix4,
  MeshStandardMaterial,
  Quaternion,
  TorusGeometry,
  Vector2,
  Vector3,
} from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { texturaDaBandaDeRodagem } from '../componentes/texturasProcedurais'

/**
 * Peças da roda de liga, fundidas por material.
 *
 * Cada roda tinha 15 malhas no conjunto rolante (pneu, aro, anel, 5 raios, 5
 * parafusos, cubo e disco), e o veículo tem quatro: 60 chamadas de desenho só
 * de roda. Os raios, e os parafusos, partilham material e vão para uma única
 * geometria cada, o que leva o conjunto a 7 malhas sem mudar um pixel: a
 * geometria é a mesma, só que já posicionada, e os materiais são os mesmos.
 *
 * As geometrias e os materiais ficam em cache por dimensão, então as quatro
 * rodas de um veículo (e as de todos os veículos do mesmo perfil) dividem os
 * mesmos objetos. O cache não é descartado: são poucas combinações, uma por
 * perfil de veículo.
 */
export interface ParteDaRoda {
  readonly chave: string
  readonly geometria: BufferGeometry
  readonly material: Material
}

const UM = new Vector3(1, 1, 1)

/** Intensidade do relevo da banda de rodagem, em unidades do `bumpScale` do three. */
const BUMP_DA_BANDA = 2

/** `<mesh position rotation>` dentro de `<group rotation={[0, giroY, 0]}>`, já como matriz. */
function matriz(posicao: [number, number, number], rotacao: [number, number, number] = [0, 0, 0], giroY = 0): Matrix4 {
  const local = new Matrix4().compose(
    new Vector3(...posicao),
    new Quaternion().setFromEuler(new Euler(...rotacao, 'XYZ')),
    UM,
  )
  return giroY === 0 ? local : new Matrix4().makeRotationY(giroY).multiply(local)
}

function posicionar(geometria: BufferGeometry, m: Matrix4): BufferGeometry {
  return geometria.applyMatrix4(m)
}

/** Funde geometrias de mesmos atributos; só falha se os atributos divergirem, o que seria erro daqui. */
function fundir(geometrias: BufferGeometry[]): BufferGeometry {
  const fundida = mergeGeometries(geometrias)
  if (!fundida) throw new Error('partesDaRoda: geometrias com atributos incompatíveis')
  geometrias.forEach((g) => g.dispose())
  return fundida
}

function perfilDoPneu(aro: number, raio: number, largura: number): Vector2[] {
  return ([
    [aro, -largura * 0.48], [raio * 0.87, -largura * 0.52], [raio * 0.98, -largura * 0.4],
    [raio, -largura * 0.28], [raio, largura * 0.28], [raio * 0.98, largura * 0.4],
    [raio * 0.87, largura * 0.52], [aro, largura * 0.48],
  ] as const).map(([x, y]) => new Vector2(x, y))
}

function construir(raio: number, largura: number, simplificada: boolean): ParteDaRoda[] {
  const aro = raio * 0.68
  const face = -largura * 0.49
  const raios = Array.from({ length: 5 }, (_, i) => (i * Math.PI * 2) / 5)

  const material = (cor: string, metalico: number, rugosidade: number, dupla = false) =>
    new MeshStandardMaterial({ color: cor, metalness: metalico, roughness: rugosidade, ...(dupla ? { side: DoubleSide } : {}) })

  const partes: ParteDaRoda[] = [
    {
      chave: 'pneu',
      geometria: new LatheGeometry(perfilDoPneu(aro, raio, largura), simplificada ? 16 : 32),
      // o relevo da banda de rodagem é só sombreamento: a cor do pneu não muda
      material: Object.assign(material('#20252b', 0, 0.92, true), {
        bumpMap: texturaDaBandaDeRodagem(),
        bumpScale: BUMP_DA_BANDA,
      }),
    },
    {
      chave: 'aro',
      geometria: new CylinderGeometry(aro, aro, largura * 0.9, 24, 1, true),
      material: material('#929da8', 0.8, 0.3, true),
    },
    {
      chave: 'anel',
      geometria: posicionar(new TorusGeometry(aro, raio * 0.027, 8, 32), matriz([0, face, 0], [Math.PI / 2, 0, 0])),
      material: material('#c8d3df', 0.86, 0.23),
    },
    {
      chave: 'raios',
      geometria: fundir(
        raios.map((a) =>
          posicionar(
            new BoxGeometry(aro * 0.17, largura * 0.07, aro * 0.77),
            matriz([0, face, aro * 0.55], [0, 0.12, 0], a),
          ),
        ),
      ),
      material: material('#bac8d6', 0.82, 0.26),
    },
    {
      chave: 'cubo',
      geometria: posicionar(new CylinderGeometry(aro * 0.27, aro * 0.27, largura * 0.1, 16), matriz([0, face, 0])),
      material: material('#455566', 0.75, 0.3),
    },
  ]

  if (!simplificada) {
    partes.push(
      {
        chave: 'parafusos',
        geometria: fundir(
          raios.map((a) =>
            posicionar(
              new CylinderGeometry(raio * 0.018, raio * 0.018, 0.009, 6),
              matriz([0, face - 0.008, aro * 0.22], [0, 0, 0], a),
            ),
          ),
        ),
        material: material('#dce3ea', 0.9, 0.2),
      },
      {
        chave: 'disco',
        geometria: posicionar(new CylinderGeometry(aro * 0.79, aro * 0.79, 0.012, 32), matriz([0, -largura * 0.15, 0])),
        material: material('#64717c', 0.75, 0.46),
      },
    )
  }

  return partes
}

const CACHE = new Map<string, readonly ParteDaRoda[]>()

/** Partes do conjunto rolante para estas dimensões, dividindo geometria e material entre as rodas. */
export function partesDaRoda(raio: number, largura: number, simplificada: boolean): readonly ParteDaRoda[] {
  const chave = `${raio}|${largura}|${simplificada}`
  let partes = CACHE.get(chave)
  if (!partes) {
    partes = construir(raio, largura, simplificada)
    CACHE.set(chave, partes)
  }
  return partes
}
